// React
import { useState, useEffect } from "react";

// OpenLayers
import OlMap from "ol/Map";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Feature from "ol/Feature";
import Geometry from "ol/geom/Geometry";

// Classes
import { Popup, PopupProps } from "lib/classes/popup";

// Tools
import { assetURL } from "lib/utilities/asset";
import { getPolygonHa } from "lib/utilities/feature";

// Lang
import { getLocaleString } from "lib/localization/vendor";

// Styles
import "styles/css/Filter.scss";

const FilterPopup = new Popup({
	title: () => getLocaleString("filter"),
	render: function() {
		const [originDrawn, setOriginDrawn] = useState<boolean>(true);
		const [originUploaded, setOriginUploaded] = useState<boolean>(true);
		const [originExistingSite, setOriginExistingSite] = useState<boolean>(true);
		const [typePolygon, setTypePolygon] = useState<boolean>(true);
		const [typePoint, setTypePoint] = useState<boolean>(true);
		const [typeLine, setTypeLine] = useState<boolean>(true);
		const [areaMax, setAreaMax] = useState<number>(1000000);
		const [areaMin, setAreaMin] = useState<number>(0);
		const [formId, setFormId] = useState<number>();
		
		// Map	
		const olMap:OlMap = this.get("map");
		
		// Layers to filter with
		const sitesLayer:VectorLayer<VectorSource> = olMap.get("sites-layer");
		const drawingLayer:VectorLayer<VectorSource> = olMap.get("drawing-layer");
			
		useEffect(() => {
			updateFilter();
		}, [originDrawn, originUploaded, originExistingSite, typePolygon, typePoint, typeLine, areaMax, areaMin, formId]);
		
		// Get all the features to filter with
		const getAllFilterFeatures = ():Feature<Geometry>[] => {
			return [
				...sitesLayer.getSource().getFeatures(),
				...drawingLayer.getSource().getFeatures()
			];
		}
		
		// Update the filter features
		const updateFilter = () => {
			const features:Feature<Geometry>[] = getAllFilterFeatures();
				
			_events.invoke("deselect-all-features");
				
			features.forEach((feature:Feature<Geometry>) => {
				const areaHa:number = feature.get("area-ha") || getPolygonHa(feature.getGeometry());
				const type:string = feature.getGeometry()?.getType()?.replace("Multi", "");
				
				const correctType:boolean = (
					(type == "Polygon" && typePolygon) || 
					(type == "Point" && typePoint) || 
					(type == "LineString" && typeLine)
				);
				const correctFormId:boolean = feature.get("existing-site")?.formId == formId || !formId;
				const correctArea:boolean = (areaHa < areaMax && areaHa > areaMin) || !areaHa;
				
				let showOrigin:boolean = true;
				
				switch(feature.get("origin")) {
					case "drawn":
						showOrigin = originDrawn;
					break;
					case "uploaded":
						showOrigin = originUploaded;
					break;
					default:
						if(feature.get("existing-site"))
							showOrigin = originExistingSite;
					break;
				}
				
				const show:boolean = showOrigin && correctType && correctFormId && correctArea;
				
				feature.set("filter-show", show);
				feature.setStyle(show ? null : () => {});
			});
		}
		
		return (
			<>
				<div className="filter-section">
					<div className="filter-field">
						<b>{getLocaleString("filterorigin")}</b>
						
						<div className="filter-field--option prevent-select" onClick={() => setOriginDrawn(!originDrawn)}>
							<input
								type="checkbox"
								checked={originDrawn}
								onChange={() => {}}
							/> 
							{getLocaleString("drawn")}
						</div>
						
						<div className="filter-field--option prevent-select" onClick={() => setOriginUploaded(!originUploaded)}>
							<input
								type="checkbox"
								checked={originUploaded}
								onChange={() => {}}
							/> 
							{getLocaleString("uploaded")}
						</div>
						
						<div className="filter-field--option prevent-select" onClick={() => setOriginExistingSite(!originExistingSite)}>
							<input
								type="checkbox"
								checked={originExistingSite}
								onChange={() => {}}
							/> 
							{getLocaleString("existingsite")}
						</div>
					</div>
					
					<div className="filter-field">
						<b>{getLocaleString("filtertype")}</b>
						
						<div className="filter-field--option prevent-select" onClick={() => setTypePolygon(!typePolygon)}>
							<input
								type="checkbox"
								checked={typePolygon}
								onChange={() => {}}
							/> 
							{getLocaleString("polygon")}
						</div>
						
						<div className="filter-field--option prevent-select" onClick={() => setTypePoint(!typePoint)}>
							<input
								type="checkbox"
								checked={typePoint}
								onChange={() => {}}
							/> 
							{getLocaleString("point")}
						</div>
						
						<div className="filter-field--option prevent-select" onClick={() => setTypeLine(!typeLine)}>
							<input
								type="checkbox"
								checked={typeLine}
								onChange={() => {}}
							/> 
							{getLocaleString("linestring")}
						</div>
					</div>
				</div>
				
				<div className="filter-section">
					<div className="filter-field">
						<b>Filter Area (ha)</b>
						
						<div className="filter-field--option">
							<table>
								<tbody>
									<tr>
										<td>
											{getLocaleString("minimum")}
										</td>
										<td>
											<input 
												type="number"
												value={areaMin}
												onChange={(e:any) => setAreaMin(e.target.value)}
											/> 
										</td>
									</tr>
									<tr>
										<td>
											{getLocaleString("maximum")}
										</td>
										<td>
											<input 
												type="number"
												value={areaMax}
												onChange={(e:any) => setAreaMax(e.target.value)}
											/> 
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
					
					<div className="filter-field">
						<b>{getLocaleString("filterformid")}</b>
						
						<div className="filter-field--option">
							<input 
								type="number"
								value={formId || ""}
								placeholder={getLocaleString("formid")}
								onChange={(e:any) => setFormId(Number(e.target.value))}
							/> 
							<button onClick={() => setFormId(undefined)} style={{marginLeft: 5}}>
								{getLocaleString("clear")}
							</button>
						</div>
					</div>
				</div>
			</>
		)
	}
});

export default FilterPopup;